import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronLeft } from "lucide-react";

export function PrivacyPolicy() {
  return <LegalPage type="privacy" />;
}

export function TermsOfService() {
  return <LegalPage type="terms" />;
}

export function DataDeletion() {
  return <LegalPage type="data" />;
}

function LegalPage({ type }) {
  const lang = (typeof window !== "undefined" && localStorage.getItem("hayatak_lang")) || "ar";
  const isAr = lang === "ar";
  const content = type === "privacy"
    ? (isAr ? PRIVACY_AR : PRIVACY_EN)
    : type === "terms"
    ? (isAr ? TERMS_AR : TERMS_EN)
    : (isAr ? DATA_AR : DATA_EN);
  const title = type === "privacy"
    ? (isAr ? "سياسة الخصوصية" : "Privacy Policy")
    : type === "terms"
    ? (isAr ? "شروط الاستخدام" : "Terms of Service")
    : (isAr ? "حذف بياناتك" : "Delete Your Data");
  return (
    <div className="grain min-h-screen" dir={isAr ? "rtl" : "ltr"}>
      <div className="mood-bg" />
      <header className="glass sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 font-extrabold">
            <Sparkles size={22} style={{ color: "var(--primary)" }} />
            {isAr ? "حياتك" : "Your Life"}
          </Link>
          <Link to="/" className="btn-ghost btn" data-testid="back-home-btn">
            <ChevronLeft size={16} /> {isAr ? "الرئيسية" : "Home"}
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        <article className="glass rounded-3xl p-7 sm:p-10 prose prose-stone max-w-none">
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-2">{title}</h1>
          <p className="text-sm text-stone-500 mb-6">
            {isAr ? "آخر تحديث: ١٧ مايو ٢٠٢٦" : "Last updated: May 17, 2026"}
          </p>
          <div className="text-stone-700 leading-relaxed whitespace-pre-line">{content}</div>
        </article>
      </main>
    </div>
  );
}

const PRIVACY_AR = `
خصوصيتك مهمّة لنا جداً. هذه السياسة توضّح ما نجمعه ولماذا وكيف نحميه.

١. المعلومات التي نجمعها
- معلومات الحساب: اسمك، بريدك الإلكتروني، عمرك، جنسك، بلدك، اهتماماتك، اسم رفيقك الذكي، لغتك المفضّلة. نجمع هذه المعلومات فقط لتخصيص تجربتك.
- محتوى المحادثات: رسائلك مع رفيقك الذكي تُحفظ في قاعدة بياناتنا لتمكين الذاكرة السياقية.
- اليوميات والمزاج والأهداف: تُحفظ بأمان في حسابك فقط.
- بيانات تقنية: نوع المتصفح والجهاز لأغراض الأمان والأداء.

٢. ما لا نجمعه
- لا نجمع موقعك الجغرافي.
- لا نطلب الوصول إلى الكاميرا أو الميكروفون أو جهات الاتصال.
- لا نجمع أي معلومات بنكية (التطبيق مجاني تماماً).

٣. كيف نستخدم معلوماتك
- لتخصيص ردود رفيقك الذكي حسب ملفك الشخصي.
- لإظهار إحصائيات مزاجك وتحليلك الأسبوعي.
- لتأمين حسابك ومنع الإساءة.

٤. مشاركة المعلومات
- نحن لا نبيع بياناتك أبداً.
- نحن لا نشاركها مع معلنين أو شركات تسويق.
- لتشغيل الذكاء الاصطناعي، تُرسل رسائلك إلى مزود الذكاء الاصطناعي (Anthropic Claude) عبر اتصال مشفّر. لا تُستخدم بياناتك لتدريب نماذجهم.

٥. الأمان
- جميع الاتصالات مشفّرة عبر HTTPS.
- كلمات المرور مشفّرة باستخدام bcrypt.
- لا نخزّن كلمة مرورك بنص واضح.

٦. حقوقك
- يمكنك تعديل معلوماتك من إعدادات الملف الشخصي.
- يمكنك مسح محادثاتك في أي وقت.
- يمكنك طلب حذف حسابك بالكامل بالكتابة إلى support@yourlife.social.
- نحذف بياناتك خلال ٣٠ يوماً من طلبك.

٧. الأطفال
- التطبيق غير مخصص للأطفال دون سن ١٣ عاماً.
- إذا اكتشفنا حساباً لطفل أصغر، نحذفه فوراً.

٨. السلامة والمحتوى
- الرفيق الذكي مدرّب على رفض المحتوى العنيف أو الضار.
- في حالة الإشارة إلى أفكار إيذاء النفس، يحثّك التطبيق على الاتصال بخط دعم نفسي محلي.
- التطبيق ليس بديلاً عن العلاج النفسي المتخصّص.

٩. تغييرات على هذه السياسة
- قد نُحدّث هذه السياسة من حين لآخر. سنخبرك بأي تغييرات جوهرية عبر التطبيق.

١٠. التواصل
- للأسئلة: support@yourlife.social
- لحذف الحساب: privacy@yourlife.social
`.trim();

const PRIVACY_EN = `
Your privacy matters deeply to us. This policy explains what we collect, why, and how we protect it.

1. Information We Collect
- Account info: your name, email, age, gender, country, interests, companion name, preferred language. Used only to personalize your experience.
- Conversation content: your messages with your AI companion are stored in our database to enable contextual memory.
- Journal, mood, and goals: stored securely under your account only.
- Technical data: browser/device type for security and performance.

2. What We Don't Collect
- We don't collect your geographic location.
- We don't request camera, microphone, or contacts access.
- We don't collect any payment info (the app is fully free).

3. How We Use Your Information
- To personalize your AI companion's responses based on your profile.
- To show your mood stats and weekly reflection.
- To secure your account and prevent abuse.

4. Sharing
- We never sell your data.
- We never share it with advertisers or marketers.
- To run the AI, your messages are sent to our AI provider (Anthropic Claude) over an encrypted connection. Your data is NOT used to train their models.

5. Security
- All communications are encrypted via HTTPS.
- Passwords are hashed using bcrypt.
- We never store passwords in plain text.

6. Your Rights
- You can edit your info from profile settings.
- You can clear your chat history anytime.
- You can request full account deletion by emailing support@yourlife.social.
- We delete your data within 30 days of your request.

7. Children
- The app is not intended for children under 13.
- If we discover a younger child's account, we delete it immediately.

8. Safety & Content
- The AI companion is trained to refuse violent or harmful content.
- If self-harm thoughts are mentioned, the app urges you to contact a local crisis hotline.
- The app is not a substitute for professional mental health care.

9. Changes to This Policy
- We may update this policy occasionally. We'll notify you of any material changes in-app.

10. Contact
- Questions: support@yourlife.social
- Account deletion: privacy@yourlife.social
`.trim();

const TERMS_AR = `
بدخولك إلى تطبيق «حياتك» (Your Life)، فإنك توافق على هذه الشروط.

١. القبول
باستخدامك للتطبيق، فأنت توافق على هذه الشروط وعلى سياسة الخصوصية.

٢. وصف الخدمة
«حياتك» تطبيق رفيق شخصي مدعوم بالذكاء الاصطناعي يقدّم محادثة، يوميات، تتبّع مزاج، وإدارة أهداف. الخدمة مقدّمة كما هي بدون ضمانات.

٣. الأهلية
- يجب أن تكون في عمر ١٣ سنة فأكثر لاستخدام التطبيق.
- إذا كنت دون ١٨ سنة، يجب الحصول على إذن وليّك.

٤. حسابك
- أنت مسؤول عن الحفاظ على سرية كلمة مرورك.
- أنت مسؤول عن كل النشاط الذي يتم من حسابك.
- يحقّ لنا تعليق أي حساب يخالف هذه الشروط.

٥. الاستخدام المقبول
يُمنع استخدام التطبيق لـ:
- نشر محتوى عنيف أو غير قانوني أو مسيء.
- محاولة اختراق التطبيق أو إساءة استخدامه.
- انتحال شخصية أحد.
- استخدامه لأغراض تجارية بدون إذن.

٦. الذكاء الاصطناعي والسلامة
- ردود الرفيق الذكي تُولّد آلياً وقد تحتوي على أخطاء.
- التطبيق ليس بديلاً عن الاستشارة الطبية أو النفسية المتخصّصة.
- في حالات الطوارئ النفسية، اتصل بخط دعم محلي مباشرة.

٧. الملكية الفكرية
- التطبيق وأيقوناته وتصميمه ملك لمالك «حياتك».
- محتواك (يومياتك، محادثاتك، أهدافك) يبقى ملكاً لك.

٨. تعديل الخدمة
يحقّ لنا تعديل أو إيقاف الخدمة في أي وقت، مع إشعار مسبق.

٩. إخلاء المسؤولية
الخدمة مقدّمة "كما هي". لا نتحمّل مسؤولية أي ضرر مباشر أو غير مباشر ناتج عن استخدام التطبيق.

١٠. القانون المعتمد
تخضع هذه الشروط لقوانين البلد الذي يعمل فيه التطبيق.

١١. التواصل
support@yourlife.social
`.trim();

const TERMS_EN = `
By accessing the "Your Life" app, you agree to these terms.

1. Acceptance
By using the app, you agree to these Terms and to our Privacy Policy.

2. Service Description
"Your Life" is a personal AI companion app offering chat, journaling, mood tracking, and goal management. The service is provided as-is without warranties.

3. Eligibility
- You must be 13 or older to use the app.
- If under 18, you need a guardian's permission.

4. Your Account
- You are responsible for keeping your password confidential.
- You are responsible for all activity under your account.
- We may suspend any account that violates these Terms.

5. Acceptable Use
You may NOT use the app to:
- Publish violent, illegal, or offensive content.
- Hack or abuse the app.
- Impersonate others.
- Use commercially without permission.

6. AI & Safety
- AI companion responses are generated automatically and may contain errors.
- The app is NOT a substitute for professional medical or mental health advice.
- In mental health emergencies, contact a local crisis hotline directly.

7. Intellectual Property
- The app, its icons and design belong to the owner of "Your Life".
- Your content (journal, chats, goals) remains yours.

8. Service Changes
We may modify or discontinue the service at any time, with prior notice.

9. Disclaimer
The service is provided "as is". We are not liable for any direct or indirect damages from using the app.

10. Governing Law
These Terms are governed by the laws of the country where the app operates.

11. Contact
support@yourlife.social
`.trim();


const DATA_AR = `
حذف بياناتك من تطبيق «حياتك / Your Life»

نحن نحترم خصوصيتك ونمنحك السيطرة الكاملة على بياناتك. يمكنك حذف حسابك وكل بياناتك بطرق متعددة:

1. الحذف الفوري من داخل التطبيق
- سجّل دخولك إلى https://yourlife.social
- اذهب إلى صفحة "الإعدادات" أو "ملفي الشخصي"
- اضغط على "حذف الحساب"
- ستُمحى جميع بياناتك (محادثات، يوميات، مزاج، أهداف، صور، ذكريات) خلال 24 ساعة.

2. الحذف عبر البريد الإلكتروني
أرسل طلباً إلى: support@yourlife.social
- الموضوع: "طلب حذف بيانات"
- النص: ضع البريد الإلكتروني المسجّل في التطبيق
- سنُؤكّد الحذف خلال 7 أيام عمل.

3. ما الذي يُحذف؟
- معلومات حسابك (الاسم، البريد، الصورة، التفضيلات)
- جميع محادثاتك مع الذكاء الاصطناعي
- يومياتك ومزاجك المسجّل وأهدافك
- الذكريات التي بناها التطبيق عنك
- الصور التي أنشأتها وتحليلات PDF
- توكنات تسجيل الدخول الاجتماعي (Google / Facebook)

4. ما الذي قد يبقى؟
لا شيء — نحذف كل شيء نهائياً من قاعدة بياناتنا. لا نحتفظ بنسخ احتياطية تتجاوز 30 يوماً.

5. الأسئلة؟
للتواصل: support@yourlife.social
`.trim();

const DATA_EN = `
Delete Your Data — «Your Life / حياتك»

We respect your privacy and give you full control over your data. You can delete your account and all your data through several methods:

1. Instant deletion from inside the app
- Sign in at https://yourlife.social
- Go to "Settings" or "My Profile"
- Click "Delete Account"
- All your data (chats, journals, moods, goals, images, memories) will be erased within 24 hours.

2. Deletion by email
Send a request to: support@yourlife.social
- Subject: "Data deletion request"
- Body: include the email you registered with
- We will confirm the deletion within 7 business days.

3. What gets deleted?
- Your account info (name, email, picture, preferences)
- All your AI chat history
- Your journals, mood logs, and goals
- The memories the app built about you
- Generated images and PDF analyses
- Social sign-in tokens (Google / Facebook)

4. What remains?
Nothing — we erase everything permanently from our database. No backups beyond 30 days.

5. Questions?
Contact: support@yourlife.social
`.trim();
